import { motion, type HTMLMotionProps } from "motion/react";
import { useEffect, useState, type ElementType, type ReactNode } from "react";

/* ─────────────────  FadeIn (single element entrance)  ───────────────── */

type FadeInProps = Omit<HTMLMotionProps<"div">, "as"> & {
  children: ReactNode;
  delay?: number;
  y?: number;
  once?: boolean;
  as?: ElementType;
};

export function FadeIn({
  children,
  delay = 0,
  y = 16,
  once = true,
  as = "div",
  ...rest
}: FadeInProps) {
  const Comp = motion[as as keyof typeof motion] as ElementType;
  return (
    <Comp
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.2 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      {...rest}
    >
      {children}
    </Comp>
  );
}

/* ─────────────────  Stagger (container + items)  ───────────────── */

type StaggerProps = Omit<HTMLMotionProps<"div">, "as"> & {
  children: ReactNode;
  as?: ElementType;
  delay?: number;
  gap?: number;
  className?: string;
};

/**
 * Container that staggers the entrance of its direct children. Each child
 * should be a `StaggerItem`.
 */
export function Stagger({
  children,
  as = "div",
  delay = 0,
  gap = 0.06,
  className,
  ...rest
}: StaggerProps) {
  const Comp = motion[as as keyof typeof motion] as ElementType;
  return (
    <Comp
      initial="hidden"
      animate="show"
      variants={{
        hidden: {},
        show: {
          transition: { staggerChildren: gap, delayChildren: delay },
        },
      }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  );
}

type StaggerItemProps = Omit<HTMLMotionProps<"div">, "as"> & {
  children: ReactNode;
  as?: ElementType;
  y?: number;
  className?: string;
};

export function StaggerItem({
  children,
  as = "div",
  y = 12,
  className,
  ...rest
}: StaggerItemProps) {
  const Comp = motion[as as keyof typeof motion] as ElementType;
  return (
    <Comp
      variants={{
        hidden: { opacity: 0, y },
        show: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
        },
      }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  );
}

/* ─────────────────  Page transition (M3 container transform)  ───────────────── */

export function PageTransition({
  children,
  id,
  className,
}: {
  children: ReactNode;
  id: string;
  /** Optional className for the wrapper (e.g. `flex-1 min-h-0 flex
   *  flex-col` so the child module can stretch to fill <main> on
   *  routes like FamChat that want a full-viewport surface). */
  className?: string;
}) {
  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
/* ─────────────────  Counter (number tween)  ───────────────── */

export function Counter({
  to,
  prefix = "",
  suffix = "",
  duration = 1.2,
  className,
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  className?: string;
}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      setValue(Math.round(to * ease(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);

  return (
    <span className={className}>
      {prefix}
      {value}
      {suffix}
    </span>
  );
}
